import {
  ListItemButton,
  Checkbox,
  ListItemText,
  Box,
  Divider,
  styled,
  Chip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import React from "react";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import { InboxType } from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import ThreadIcon from "~frontend/components/threads/ThreadIcon";
import { useThreadsContext } from "~frontend/components/threads/ThreadsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { palette } from "~frontend/theme";
import formatContactName from "~frontend/util/formatContactName";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import { BulkSelectionState } from "./types";

const CampaignName = styled("div")({
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const LastSentAt = styled("div")({
  marginLeft: "16px",
});

export default function ThreadListItems({
  bulkSelectionState,
  setBulkSelectionState,
}: {
  bulkSelectionState: BulkSelectionState;
  setBulkSelectionState: (state: BulkSelectionState) => void;
}): ReactElement {
  const { threadId, setThreadAndContext, threadsList, loadingThreads } =
    useThreadsContext()!;

  const { formatMessage } = useIntl();

  if (threadsList.loading) {
    return <Loading />;
  }

  if (threadsList.error) {
    return <ErrorCard errors={[threadsList.error]} />;
  }

  return (
    <>
      {threadsList.data!.map(row => (
        <React.Fragment key={row.id}>
          <ListItemButton
            data-thread-id={row.id}
            selected={row.id === threadId}
            onClick={() => {
              if (bulkSelectionState.state === "inactive") {
                setThreadAndContext(
                  row.id,
                  row.defaultContextCampaign?.id ?? null
                );
              } else if (
                bulkSelectionState.selectedThreadIds.includes(row.id)
              ) {
                setBulkSelectionState({
                  ...bulkSelectionState,
                  selectedThreadIds:
                    bulkSelectionState.selectedThreadIds.filter(
                      id => row.id !== id
                    ),
                });
              } else {
                setBulkSelectionState({
                  ...bulkSelectionState,
                  selectedThreadIds:
                    bulkSelectionState.selectedThreadIds.concat(row.id),
                });
              }
            }}
          >
            {bulkSelectionState.state !== "inactive" && (
              <Checkbox
                checked={bulkSelectionState.selectedThreadIds.includes(row.id)}
              />
            )}
            <ThreadIcon
              isLoading={loadingThreads.includes(row.id)}
              isOptedOut={row.isOptedOut}
              needsTexterResponse={row.needsTexterResponse}
            />
            <ListItemText
              primary={
                <strong>
                  {formatContactName(
                    {
                      firstName: row.contactFirstName,
                      lastName: row.contactLastName,
                    },
                    { formatMessage }
                  )}
                </strong>
              }
              primaryTypographyProps={{ component: "div" }}
              secondary={
                <FlexBox sx={{ fontSize: "12px", color: palette.navy }}>
                  <Box>
                    <strong>{prettyPhoneNumber(row.toPhoneNumber)}</strong>{" "}
                    <FormattedMessage
                      defaultMessage="from {fromPhoneNumber}"
                      description="Sending number used for a thread"
                      values={{
                        fromPhoneNumber: prettyPhoneNumber(row.fromPhoneNumber),
                      }}
                    />
                    <br />
                    <CampaignName>
                      {row.defaultContextCampaign?.name}
                    </CampaignName>
                    {row.currentInbox &&
                    row.currentInbox.type !== InboxType.Campaign ? (
                      <Chip
                        sx={{
                          height: "15px",
                          borderRadius: 0,
                          backgroundColor:
                            row.currentInbox.type === InboxType.Escalation
                              ? palette.lightestOrange
                              : palette.lightBlue,
                          margin: 0,
                          "& .MuiChip-label": { padding: "0 4px" },
                        }}
                        label={
                          <Typography>
                            {row.currentInbox.type === InboxType.Escalation ? (
                              <Typography variant="label2Caps" component="span">
                                <FormattedMessage
                                  defaultMessage="Escalation:"
                                  description="Prefix for the thread escalation label"
                                />
                              </Typography>
                            ) : undefined}
                            <Typography variant="body2" component="span">
                              {" "}
                              {row.currentInbox.name}
                            </Typography>
                          </Typography>
                        }
                      />
                    ) : undefined}
                  </Box>
                  <LastSentAt>
                    {DateTime.fromMillis(row.lastMessageAt).toLocaleString(
                      DateTime.DATETIME_SHORT
                    )}
                  </LastSentAt>
                </FlexBox>
              }
              secondaryTypographyProps={{ component: "div" }}
            />
          </ListItemButton>
          <Divider component="li" />
        </React.Fragment>
      ))}
    </>
  );
}
